/**
 * Full Restore Cards — Restoration Candidacy Quiz
 * Presentation layer. Owns the flow, transitions and DOM only.
 *
 * This file NEVER decides a verdict itself — it calls the assess()
 * function from restoration-quiz-engine.js (or whatever is passed in via
 * options.assess) and only formats what comes back. Load order in the
 * page/theme: link the stylesheet, then load restoration-quiz-engine.js,
 * then restoration-quiz-ui.js, then mount it against a container element:
 *   new RestorationQuiz(document.getElementById('frc-quiz'));
 *
 * Options:
 *   assess(answers, config)  — defaults to window.RestorationQuizEngine.assess
 *   config                   — passed straight through to assess()
 *   onSubmit({answers, result, contact, photos}) — called on final submit.
 *       Defaults to a console log + alert, since the lead-capture backend
 *       (Shopify contact form vs a form tool) is a separate, later decision.
 */
(function () {
  'use strict';

  var ICONS = {
    back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="16" height="16"><path d="M15 18l-6-6 6-6"/></svg>',
    close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><path d="M18 6L6 18M6 6l12 12"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>',
    dot: '<svg viewBox="0 0 24 24" fill="currentColor" width="6" height="6"><circle cx="12" cy="12" r="12"/></svg>',
    card: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.2"/><path d="M3 10h18"/></svg>',
    loupe: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M20 20l-4.8-4.8"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8.5"/><path d="M12 11v5M12 8.2v.2"/></svg>',
    upload: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" width="20" height="20"><path d="M12 15V4M8 8l4-4 4 4"/><path d="M4 15v3a2 2 0 002 2h12a2 2 0 002-2v-3"/></svg>',
    cardRaw: '<svg viewBox="0 0 64 80" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="6" width="52" height="68" rx="6"/><rect x="16" y="18" width="32" height="24" rx="2"/><path d="M16 50h32M16 58h20"/></svg>',
    cardGraded: '<svg viewBox="0 0 72 80" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="4" width="64" height="72" rx="7"/><rect x="10" y="10" width="52" height="60" rx="4"/><rect x="16" y="16" width="40" height="9" rx="2"/><rect x="20" y="32" width="32" height="22" rx="2"/><path d="M20 60h32"/></svg>',
  };

  var HERO_ICONS = { raw: ICONS.cardRaw, graded: ICONS.cardGraded };

  var GENERIC_DAMAGE = ['multiple_unspecified', 'other'];

  // -----------------------------------------------------------------
  // Raw vs Graded goal options (fixes: Raw could previously still show/
  // select "Regrade an already graded card"). GOAL_OPTIONS_ALL is the
  // master list; goalOptionsFor() filters it per q2_status.
  // -----------------------------------------------------------------
  var GOAL_OPTIONS_ALL = [
    ['binder_display', 'Keep it in a binder or display'],
    ['submit_grading', 'Submit it for grading'],
    ['regrade', 'Regrade an already graded card'],
    ['sell', 'Sell it'],
    ['not_sure', 'Not sure yet'],
  ];
  var RAW_GOALS = ['binder_display', 'submit_grading', 'sell', 'not_sure'];
  var GRADED_GOALS = ['binder_display', 'regrade', 'sell', 'not_sure'];

  function goalOptionsFor(status) {
    var allowed = status === 'raw' ? RAW_GOALS : (status === 'graded' ? GRADED_GOALS : null);
    if (!allowed) return GOAL_OPTIONS_ALL;
    return GOAL_OPTIONS_ALL.filter(function (o) { return allowed.indexOf(o[0]) !== -1; });
  }

  // -----------------------------------------------------------------
  // Damage-type conditional "queue" — supports chained conditional
  // questions (location, then a holo sub-question) for crease/indent, and
  // a single holo question for bubble_crease, without touching the
  // existing single-`conditional` mechanism used by q2/q3/q8 below. Every
  // screen here reuses the same topbar + option-list rendering as the
  // existing conditional screens, so no new CSS is required.
  // -----------------------------------------------------------------
  var HOLO_OPTIONS = [['holo', 'Holographic area'], ['non_holo', 'Non-holographic area'], ['both', 'Both'], ['not_sure', 'Not sure']];
  var LOCATION_OPTIONS = [['front', 'Front'], ['back', 'Back'], ['both', 'Both'], ['not_sure', 'Not sure']];

  function isFrontOrBoth(v) { return v === 'front' || v === 'both'; }

  var DAMAGE_CONDITIONALS = {
    crease: {
      first: {
        id: 'q4a_crease_location', subLabel: 'Crease location',
        text: 'Where is the crease visible?', options: LOCATION_OPTIONS,
      },
      followUp: {
        id: 'q4a_crease_holo', subLabel: 'Crease — front area',
        text: 'Where does the crease affect the front of the card?', options: HOLO_OPTIONS,
        appliesWhen: isFrontOrBoth,
      },
    },
    indent: {
      first: {
        id: 'q4b_indent_location', subLabel: 'Indent location',
        text: 'Where is the indent?', options: LOCATION_OPTIONS,
      },
      followUp: {
        id: 'q4b_indent_holo', subLabel: 'Indent — front area',
        text: 'Where does the indent affect the front of the card?', options: HOLO_OPTIONS,
        appliesWhen: isFrontOrBoth,
      },
    },
    bubble_crease: {
      first: {
        id: 'q4c_bubble_crease_holo', subLabel: 'Bubble crease location',
        text: 'Where is the bubble crease?', options: HOLO_OPTIONS,
      },
      followUp: null,
    },
  };
  // Order in which selected types are asked about, when more than one of
  // these types is selected at once.
  var DAMAGE_CONDITIONAL_ORDER = ['crease', 'indent', 'bubble_crease'];
  var DAMAGE_GROUP_BY_FIRST_ID = {
    q4a_crease_location: DAMAGE_CONDITIONALS.crease,
    q4b_indent_location: DAMAGE_CONDITIONALS.indent,
  };

  // Builds the ordered list of damage-queue steps that still need answering
  // (or were already answered, when reconstructing on back()) for the
  // currently selected damage types.
  function buildDamageQueue(selectedTypes, answers) {
    var queue = [];
    var types = selectedTypes || [];
    DAMAGE_CONDITIONAL_ORDER.forEach(function (type) {
      if (types.indexOf(type) === -1) return;
      var group = DAMAGE_CONDITIONALS[type];
      queue.push(group.first);
      if (group.followUp) {
        var firstAnswer = answers[group.first.id];
        if (firstAnswer !== undefined && group.followUp.appliesWhen(firstAnswer)) {
          queue.push(group.followUp);
        }
      }
    });
    return queue;
  }

  // Deletes any damage-conditional answers belonging to a type that is no
  // longer selected, so stale/invalid child answers never linger.
  function clearOrphanedDamageAnswers(selectedTypes, answers) {
    var types = selectedTypes || [];
    DAMAGE_CONDITIONAL_ORDER.forEach(function (type) {
      if (types.indexOf(type) !== -1) return;
      var group = DAMAGE_CONDITIONALS[type];
      delete answers[group.first.id];
      if (group.followUp) delete answers[group.followUp.id];
    });
  }

  var QUESTIONS = [
    {
      id: 'q1_value', step: 1, type: 'list',
      text: "Roughly what's the card worth right now, as is?",
      options: [
        ['under100', 'Under $100'], ['100_300', '$100 to $300'], ['300_500', '$300 to $500'],
        ['500_1000', '$500 to $1,000'], ['1000_2500', '$1,000 to $2,500'], ['2500_plus', '$2,500+'], ['not_sure', 'Not sure'],
      ],
    },
    {
      id: 'q2_status', step: 2, type: 'hero2',
      text: 'Is the card currently raw or graded?',
      options: [['raw', 'Raw'], ['graded', 'Graded']],
      conditional: {
        id: 'q2a_current_grade', type: 'grade', subLabel: 'Current grade',
        text: 'What grade is the card currently?',
        when: function (v) { return v === 'graded'; },
        extra: [['authentic', 'Authentic'], ['altered_no_grade', 'Altered / No Grade'], ['other', 'Other'], ['not_sure', 'Not sure']],
      },
    },
    {
      id: 'q3_goal', step: 3, type: 'list',
      text: 'What are you hoping to do with the card afterwards?',
      // NOTE: options are resolved dynamically via goalOptionsFor(q2_status)
      // in _renderQuestion — this static list is the fallback only.
      options: GOAL_OPTIONS_ALL,
      conditional: {
        id: 'q3a_sell_plan', type: 'list', subLabel: 'How you plan to sell it',
        text: 'How are you planning to sell it?',
        when: function (v) { return v === 'sell'; },
        options: [['raw_ungraded', 'Raw, ungraded'], ['grade_before_selling', 'Grade it before selling'], ['not_sure', 'Not sure yet']],
      },
    },
    {
      id: 'q4_damage_types', step: 4, type: 'multi',
      text: 'What damage does the card have?',
      options: [
        ['crease', 'Crease'], ['bubble_crease', 'Bubble crease'], ['indent', 'Indent'], ['edge_lift', 'Edge lift'],
        ['delamination', 'Delamination'], ['warping', 'Warping'], ['contamination', 'Surface dirt'], ['scratching', 'Scratching'],
        ['corner_edge_wear', 'Corner/edge wear'], ['corner_edge_structural', 'Structural corner/edge'],
        ['multiple_unspecified', 'Multiple types / not sure which'], ['other', 'Something else'],
      ],
    },
    {
      id: 'q5_whitening', step: 5, type: 'list',
      text: 'How much whitening or general wear does the card have?',
      options: [['none', 'None or barely any'], ['light', 'Light'], ['moderate', 'Moderate'], ['heavy', 'Heavy'], ['very_heavy', 'Very heavy'], ['not_sure', 'Not sure']],
    },
    {
      id: 'q6_overall_condition', step: 6, type: 'list',
      text: 'How would you describe the card overall, aside from the main damage?',
      options: [['very_clean', 'Very clean'], ['clean', 'Clean'], ['moderate_wear', 'Moderate wear'], ['heavy_wear', 'Heavy wear'], ['very_heavily_played', 'Very heavily played'], ['not_sure', 'Not sure']],
    },
    {
      id: 'q7_main_severity', step: 7, type: 'severity',
      text: 'How severe is the main damage?',
      options: [['light', 'Light'], ['moderate', 'Moderate'], ['heavy', 'Heavy'], ['severe', 'Severe']],
      extra: [['not_sure', 'Not sure']],
    },
    {
      id: 'q8_expectation', step: 8, type: 'list',
      text: 'What would make you happy with the restoration?',
      options: [
        ['noticeably_better', 'Noticeably better'], ['reduced_as_much_as_possible', 'Damage reduced as much as possible'],
        ['best_possible', 'The best possible improvement'], ['specific_grade', 'Achieve a minimum grading outcome'], ['not_sure', 'Not sure yet'],
      ],
      conditional: {
        id: 'q8a_desired_grade', type: 'list', subLabel: 'Desired grading outcome',
        text: 'What grading outcome are you hoping for?',
        when: function (v) { return v === 'specific_grade'; },
        options: [
          ['min_5', '5 or better'], ['min_6', '6 or better'], ['min_7', '7 or better'],
          ['min_8', '8 or better'], ['min_9', '9 or better'], ['min_10', '10'], ['not_sure', 'Not sure'],
        ],
      },
    },
  ];
  var TOTAL_STEPS = QUESTIONS.length;

  var VERDICT_META = {
    strong: { icon: ICONS.check, eyebrow: 'Assessment result', cls: 'frcq-r-strong', ctaLabel: 'Get a Visual Assessment', sectionTitle: 'Send us your card' },
    potential: { icon: ICONS.loupe, eyebrow: 'Assessment result', cls: 'frcq-r-potential', ctaLabel: 'Get a Visual Assessment', sectionTitle: 'Send us your card' },
    probably_not: { icon: ICONS.info, eyebrow: 'Assessment result', cls: 'frcq-r-notgood', ctaLabel: 'Get a Second Opinion', sectionTitle: 'Send us your card for a second look' },
  };

  var VERDICT_TITLE = {
    strong: 'Strong Restoration Candidate',
    potential: 'Potential Restoration Candidate',
    probably_not: 'Unlikely Restoration Candidate',
  };

  var VERDICT_EXPLAIN = {
    strong: 'Based on your answers, we believe restoration has a good chance of making a meaningful improvement to this card and your expectations appear realistic.',
    potential: 'There’s restoration potential here, but we’d want to assess the card visually before making a recommendation.',
    probably_not: 'This isn’t a verdict on whether your card could ever look better. Based on what you’ve told us, restoration is unlikely to meet what you’re hoping for.',
  };

  var VERDICT_NEXT = {
    strong: 'Send us front and back photos and we’ll confirm the assessment and quote the right service.',
    potential: 'Send us front and back photos. We’ll take a proper look and give you a straight answer.',
    probably_not: 'Happy to give a second opinion if you send photos. No pressure either way.',
  };

  // -----------------------------------------------------------------
  // Turn the engine's internal gate flags (and the structured damageDetail
  // it now also returns) into 2-3 genuinely specific, customer-facing
  // reasons. This is presentation logic, not scoring — it never changes
  // the verdict, only explains the one already reached. Result explanations
  // stay honest about difficulty without themselves implying a downgrade.
  // -----------------------------------------------------------------
  function buildReasons(answers, result) {
    var f = result.flags;
    var dd = f.damageDetail || {};
    var items = [];

    // Gate 1 — expectations
    if (f.gate1.result === 'severe') {
      items.push('Your desired outcome is well beyond what the described condition supports');
    } else if (f.gate1.result === 'uncertain') {
      items.push('Whether your desired outcome is realistic depends on a closer, physical look');
    } else if (answers.q8_expectation === 'specific_grade') {
      items.push('The grading outcome you’re hoping for lines up with the condition you described');
    } else if (answers.q8_expectation === 'best_possible' || answers.q8_expectation === 'noticeably_better' || answers.q8_expectation === 'reduced_as_much_as_possible') {
      items.push('You’re looking for the best realistic improvement rather than a guaranteed grade');
    } else {
      items.push('Expectations align with the card’s condition');
    }

    // Gate 2 — cleanliness
    if (f.gate2.heavyFloor) {
      items.push('Heavy overall wear limits how much difference restoration can make');
    } else if (!f.gate2.bigMismatch) {
      items.push('Card is otherwise clean, aside from the main issue');
    }

    // Damage-detail — front/holo location and honest difficulty callouts,
    // without implying a downgrade purely from naming the difficulty.
    if (dd.frontHoloInvolved) {
      items.push('Main issue is a front holographic defect');
    }
    var mainSeverity = answers.q7_main_severity;
    if ((mainSeverity === 'heavy' || mainSeverity === 'severe') &&
      ((dd.crease && dd.crease.selected) || (dd.bubbleCrease && dd.bubbleCrease.selected))) {
      items.push('Heavy creasing can be difficult to fully remove');
    }

    // Gate 3 — main problem workability
    if (f.gate3.unfixablePresent && f.gate3.goalDependsOnFix) {
      items.push('The main issue you described isn’t something restoration meaningfully corrects');
    } else if (f.gate3.unfixablePresent) {
      items.push('One defect you mentioned is outside what restoration typically fixes, though it may not be the deciding factor');
    } else if (f.gate3.capPotential) {
      items.push('The main defect needs a physical look to judge properly');
    } else {
      items.push('Localised, workable main defect');
    }

    // Gate 4 — complexity
    if (f.gate4.unknownComplexity) {
      items.push('Unspecified damage means photos will tell us more than a questionnaire can');
    } else if (f.gate4.knownComplexity) {
      items.push('Multiple areas need individual assessment');
    }

    // Gate 5 — economics
    if (f.gate5.hardFail) {
      items.push('The likely cost may not be justified by the card’s current value for this goal');
    } else if (f.gate5.value != null && !f.gate5.hardFail && f.gate5.cost != null) {
      items.push('Card value makes professional restoration worth considering');
    }

    // De-dupe and cap at 3, keeping gate-priority order (already the order pushed).
    var seen = {};
    var unique = [];
    for (var i = 0; i < items.length; i++) {
      if (!seen[items[i]]) { seen[items[i]] = true; unique.push(items[i]); }
    }
    return unique.slice(0, 3);
  }

  function defaultOnSubmit(payload) {
    // eslint-disable-next-line no-console
    console.log('Restoration quiz submission (no backend wired yet):', payload);
    alert('Thanks! Your submission would be sent to Full Restore Cards here once the lead-capture backend is wired up.');
  }

  function RestorationQuiz(container, options) {
    options = options || {};
    this.container = container;
    this.assessFn = options.assess || (window.RestorationQuizEngine && window.RestorationQuizEngine.assess);
    this.config = options.config;
    this.onSubmit = options.onSubmit || defaultOnSubmit;

    this.state = {
      phase: 'intro',        // intro | question | analyzing | result
      qIndex: 0,
      viewingConditional: false,
      viewingDamageQueue: false,
      damageQueue: [],
      damageQueueIndex: 0,
      answers: {},
      result: null,
      submitOpen: false,
      submitAnimated: false,
      photos: { front: null, back: null },
    };

    this._root = document.createElement('div');
    this._root.className = 'frc-quiz';
    this.container.innerHTML = '';
    this.container.appendChild(this._root);

    this._render();
  }

  RestorationQuiz.prototype._q = function (idx) { return QUESTIONS[idx]; };

  // ---- navigation -----------------------------------------------------
  RestorationQuiz.prototype.start = function () {
    this.state.phase = 'question';
    this.state.qIndex = 0;
    this.state.viewingConditional = false;
    this.state.viewingDamageQueue = false;
    this._render('forward');
  };

  RestorationQuiz.prototype.back = function () {
    var s = this.state;
    if (s.phase !== 'question') return;

    if (s.viewingDamageQueue) {
      if (s.damageQueueIndex > 0) {
        s.damageQueueIndex -= 1;
      } else {
        s.viewingDamageQueue = false;
      }
      this._render('back');
      return;
    }

    if (s.viewingConditional) {
      s.viewingConditional = false;
      this._render('back');
      return;
    }
    if (s.qIndex === 0) {
      s.phase = 'intro';
      this._render('back');
      return;
    }
    s.qIndex -= 1;
    var prevQ = this._q(s.qIndex);

    if (prevQ.id === 'q4_damage_types') {
      var queue = buildDamageQueue(s.answers.q4_damage_types, s.answers);
      if (queue.length > 0) {
        s.viewingDamageQueue = true;
        s.damageQueue = queue;
        s.damageQueueIndex = queue.length - 1;
        this._render('back');
        return;
      }
      this._render('back');
      return;
    }

    var prevAnswered = s.answers[prevQ.id];
    var conditionalStillApplies = prevQ.conditional && prevAnswered !== undefined && prevQ.conditional.when(prevAnswered);
    var conditionalWasAnswered = prevQ.conditional && s.answers[prevQ.conditional.id] !== undefined;
    s.viewingConditional = !!(conditionalStillApplies && conditionalWasAnswered);
    this._render('back');
  };

  RestorationQuiz.prototype._advanceFromQuestion = function (qIndex) {
    var s = this.state;
    var q = this._q(qIndex);
    var value = s.answers[q.id];

    if (q.id === 'q4_damage_types') {
      clearOrphanedDamageAnswers(value, s.answers);
      var queue = buildDamageQueue(value, s.answers);
      if (queue.length > 0) {
        s.viewingDamageQueue = true;
        s.damageQueue = queue;
        s.damageQueueIndex = 0;
        this._render('forward');
        return;
      }
      this._goToNextQuestionOrFinish(qIndex);
      return;
    }

    if (q.conditional && q.conditional.when(value)) {
      s.viewingConditional = true;
      this._render('forward');
      return;
    }
    this._goToNextQuestionOrFinish(qIndex);
  };

  RestorationQuiz.prototype._advanceFromConditional = function (qIndex) {
    this._goToNextQuestionOrFinish(qIndex);
  };

  RestorationQuiz.prototype._goToNextQuestionOrFinish = function (qIndex) {
    var s = this.state;
    if (qIndex + 1 < TOTAL_STEPS) {
      s.qIndex = qIndex + 1;
      s.viewingConditional = false;
      this._render('forward');
    } else {
      s.phase = 'analyzing';
      this._render('forward');
      var self = this;
      var delay = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 650;
      setTimeout(function () {
        self._computeResult();
        s.phase = 'result';
        self._render('forward');
      }, delay);
    }
  };

  RestorationQuiz.prototype._computeResult = function () {
    if (typeof this.assessFn !== 'function') {
      // eslint-disable-next-line no-console
      console.error('RestorationQuiz: no assess() function available. Load restoration-quiz-engine.js first, or pass options.assess.');
      this.state.result = { verdict: 'potential', flags: {}, reasons: [], summary: '' };
      return;
    }
    this.state.result = this.assessFn(this.state.answers, this.config);
  };

  // ---- answer setters ---------------------------------------------------
  RestorationQuiz.prototype._setAnswer = function (qIndex, value) {
    var s = this.state;
    var q = this._q(qIndex);
    var prevValue = s.answers[q.id];
    s.answers[q.id] = value;

    if (q.conditional) {
      var wasApplicable = prevValue !== undefined && q.conditional.when(prevValue);
      var nowApplicable = q.conditional.when(value);
      if (wasApplicable && !nowApplicable) {
        delete s.answers[q.conditional.id];
      }
    }

    // Raw/Graded bug fix: if the goal previously selected is no longer a
    // valid option for the new status (e.g. "Regrade" was picked, then the
    // customer goes back and changes the card to Raw), clear it — and its
    // own conditional child — rather than leaving stale, invalid data.
    if (q.id === 'q2_status') {
      var allowedGoals = goalOptionsFor(value).map(function (o) { return o[0]; });
      if (s.answers.q3_goal !== undefined && allowedGoals.indexOf(s.answers.q3_goal) === -1) {
        delete s.answers.q3_goal;
        delete s.answers.q3a_sell_plan;
      }
    }

    this._advanceFromQuestion(qIndex);
  };

  RestorationQuiz.prototype._setConditionalAnswer = function (qIndex, value) {
    var q = this._q(qIndex);
    this.state.answers[q.conditional.id] = value;
    this._advanceFromConditional(qIndex);
  };

  // Answers the current damage-queue step. If it's a "first" step with a
  // follow-up, live-splices the follow-up in or out of the queue depending
  // on whether the new answer makes it applicable — auto-clearing its
  // answer if it becomes inapplicable, exactly like the existing single-
  // conditional mechanism does for q2/q3/q8.
  RestorationQuiz.prototype._setDamageQueueAnswer = function (value) {
    var s = this.state;
    var step = s.damageQueue[s.damageQueueIndex];
    s.answers[step.id] = value;

    var group = DAMAGE_GROUP_BY_FIRST_ID[step.id];
    if (group && group.followUp) {
      var idxInQueue = s.damageQueue.indexOf(step);
      var alreadyPresent = s.damageQueue.indexOf(group.followUp) !== -1;
      var nowApplies = group.followUp.appliesWhen(value);

      if (nowApplies && !alreadyPresent) {
        s.damageQueue.splice(idxInQueue + 1, 0, group.followUp);
      } else if (!nowApplies && alreadyPresent) {
        var followUpIdx = s.damageQueue.indexOf(group.followUp);
        s.damageQueue.splice(followUpIdx, 1);
        delete s.answers[group.followUp.id];
      }
    }

    s.damageQueueIndex += 1;
    if (s.damageQueueIndex < s.damageQueue.length) {
      this._render('forward');
    } else {
      s.viewingDamageQueue = false;
      this._goToNextQuestionOrFinish(s.qIndex);
    }
  };

  RestorationQuiz.prototype._toggleDamage = function (value) {
    var s = this.state;
    var arr = (s.answers.q4_damage_types || []).slice();
    var isGeneric = GENERIC_DAMAGE.indexOf(value) !== -1;

    if (isGeneric) {
      arr = arr.indexOf(value) !== -1 ? [] : [value];
    } else {
      arr = arr.filter(function (v) { return GENERIC_DAMAGE.indexOf(v) === -1; });
      var i = arr.indexOf(value);
      if (i === -1) arr.push(value); else arr.splice(i, 1);
    }
    s.answers.q4_damage_types = arr;
    this._render();
  };

  // ---- rendering ----------------------------------------------------
  RestorationQuiz.prototype._render = function () {
    var s = this.state;
    var html = '';
    if (s.phase === 'intro') html = this._renderIntro();
    else if (s.phase === 'question') {
      if (s.viewingDamageQueue) html = this._renderDamageQueueStep();
      else html = s.viewingConditional ? this._renderConditional(s.qIndex) : this._renderQuestion(s.qIndex);
    } else if (s.phase === 'analyzing') html = this._renderAnalyzing();
    else if (s.phase === 'result') html = this._renderResult();

    this._root.innerHTML = html;
    this._bind();
  };

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  RestorationQuiz.prototype._segments = function (step, isConditional) {
    var out = '';
    for (var i = 1; i <= TOTAL_STEPS; i++) {
      var cls = 'frcq-seg';
      if (i < step) cls += ' frcq-seg-done';
      else if (i === step) cls += ' frcq-seg-current' + (isConditional ? ' frcq-seg-conditional' : '');
      out += '<div class="' + cls + '"><span></span></div>';
    }
    return out;
  };

  RestorationQuiz.prototype._topbar = function (step, subLabel, showBack, isConditional) {
    return '' +
      '<div class="frcq-topbar">' +
      (showBack ? '<button class="frcq-iconbtn" data-action="back">' + ICONS.back + '</button>' : '<span style="width:32px"></span>') +
      '<div class="frcq-progress">' +
      '<div class="frcq-progress-segs">' + this._segments(step, isConditional) + '</div>' +
      '<div class="frcq-progress-label"><span class="frcq-step-hi">STEP ' + pad2(step) + '</span> / ' + pad2(TOTAL_STEPS) + (subLabel ? ' · ' + subLabel : '') + '</div>' +
      '</div>' +
      '<button class="frcq-iconbtn" title="Close" data-action="close">' + ICONS.close + '</button>' +
      '</div>';
  };

  RestorationQuiz.prototype._renderIntro = function () {
    return '' +
      '<div class="frcq-screen frcq-active frcq-intro">' +
      '<div class="frcq-mark">' + ICONS.card + '</div>' +
      '<h2>Is your card worth restoring?</h2>' +
      '<p>Answer a few quick questions about its condition and we’ll tell you where it stands.</p>' +
      '<div class="frcq-meta-row"><span class="frcq-meta-chip">' + TOTAL_STEPS + ' QUESTIONS</span><span class="frcq-meta-chip">ABOUT 90 SECONDS</span></div>' +
      '<button class="frcq-btn-primary" data-action="start">Start assessment</button>' +
      '</div>';
  };

  RestorationQuiz.prototype._optRow = function (value, label, selected, dataAttrs) {
    return '<button class="frcq-opt' + (selected ? ' frcq-selected' : '') + '" ' + dataAttrs + '>' +
      '<span>' + label + '</span><span class="frcq-tick">' + ICONS.check + '</span></button>';
  };

  RestorationQuiz.prototype._renderQuestion = function (qIndex) {
    var q = this._q(qIndex);
    var selected = this.state.answers[q.id];
    var body = '';

    if (q.type === 'list') {
      var opts = q.id === 'q3_goal' ? goalOptionsFor(this.state.answers.q2_status) : q.options;
      body = '<div class="frcq-opt-list frcq-opt-list--grid">' + opts.map(function (o) {
        return this._optRow(o[0], o[1], selected === o[0], 'data-action="answer" data-value="' + o[0] + '"');
      }, this).join('') + '</div>';
    } else if (q.type === 'hero2') {
      body = '<div class="frcq-hero2">' + q.options.map(function (o) {
        var icon = HERO_ICONS[o[0]] || '';
        return '<div class="frcq-hero-card' + (selected === o[0] ? ' frcq-selected' : '') + '" data-action="answer" data-value="' + o[0] + '">' + icon + '<span class="frcq-hero-label">' + o[1] + '</span></div>';
      }).join('') + '</div>';
    } else if (q.type === 'severity') {
      var notSure = q.extra[0];
      body = '<div class="frcq-severity">' + q.options.map(function (o) {
        return this._optRow(o[0], o[1], selected === o[0], 'data-action="answer" data-value="' + o[0] + '"');
      }, this).join('') + '</div>' +
        '<div class="frcq-severity-unsure">' + this._optRow(notSure[0], notSure[1], selected === notSure[0], 'data-action="answer" data-value="' + notSure[0] + '"') + '</div>' +
        '<p class="frcq-severity-note">This is about how serious the main defect looks, not the whole card.</p>';
    } else if (q.type === 'multi') {
      var chosen = this.state.answers.q4_damage_types || [];
      body = '<div class="frcq-multi-grid">' + q.options.map(function (o) {
        var isSel = chosen.indexOf(o[0]) !== -1;
        return '<div class="frcq-chip' + (isSel ? ' frcq-selected' : '') + '" data-action="toggle-damage" data-value="' + o[0] + '">' +
          '<span class="frcq-box">' + ICONS.check + '</span><span>' + o[1] + '</span></div>';
      }).join('') + '</div>' +
        '<div class="frcq-q-footer"><button class="frcq-btn-primary" data-action="continue-multi" ' + (chosen.length ? '' : 'disabled') + '>Continue</button></div>';
    }

    return '<div class="frcq-screen frcq-active">' +
      this._topbar(q.step, null, true, false) +
      '<div class="frcq-q-body"><div class="frcq-q-text">' + q.text + '</div>' + body + '</div>' +
      '</div>';
  };

  RestorationQuiz.prototype._renderConditional = function (qIndex) {
    var q = this._q(qIndex);
    var c = q.conditional;
    var selected = this.state.answers[c.id];
    var body = '';

    if (c.type === 'grade') {
      var cells = [];
      for (var n = 1; n <= 10; n++) cells.push(String(n));
      body = '<div class="frcq-grade-grid">' + cells.map(function (n) {
        return '<div class="frcq-grade-cell' + (selected === n ? ' frcq-selected' : '') + '" data-action="answer-conditional" data-value="' + n + '">' + n + '</div>';
      }).join('') + '</div>' +
        '<div class="frcq-grade-extra">' + c.extra.map(function (o) {
          return this._optRow(o[0], o[1], selected === o[0], 'data-action="answer-conditional" data-value="' + o[0] + '"');
        }, this).join('') + '</div>';
    } else if (c.type === 'list') {
      body = '<div class="frcq-opt-list">' + c.options.map(function (o) {
        return this._optRow(o[0], o[1], selected === o[0], 'data-action="answer-conditional" data-value="' + o[0] + '"');
      }, this).join('') + '</div>';
    }

    return '<div class="frcq-screen frcq-active">' +
      this._topbar(q.step, c.subLabel, true, true) +
      '<div class="frcq-q-body"><div class="frcq-q-text">' + c.text + '</div>' + body + '</div>' +
      '</div>';
  };

  // Renders the current step of the q4 damage-conditional queue. Reuses the
  // exact same topbar/option-list structure as _renderConditional, anchored
  // to q4's own step number, so it's visually indistinguishable from the
  // existing conditional screens and needs no new CSS.
  RestorationQuiz.prototype._renderDamageQueueStep = function () {
    var s = this.state;
    var step = s.damageQueue[s.damageQueueIndex];
    var q4 = QUESTIONS[3]; // q4_damage_types, index fixed by position in QUESTIONS
    var selected = s.answers[step.id];

    var body = '<div class="frcq-opt-list">' + step.options.map(function (o) {
      return this._optRow(o[0], o[1], selected === o[0], 'data-action="answer-damage-queue" data-value="' + o[0] + '"');
    }, this).join('') + '</div>';

    return '<div class="frcq-screen frcq-active">' +
      this._topbar(q4.step, step.subLabel, true, true) +
      '<div class="frcq-q-body"><div class="frcq-q-text">' + step.text + '</div>' + body + '</div>' +
      '</div>';
  };

  RestorationQuiz.prototype._renderAnalyzing = function () {
    return '<div class="frcq-screen frcq-active frcq-analyzing">' +
      '<div class="frcq-scan">' + ICONS.card + '</div>' +
      '<p>Reviewing your answers</p>' +
      '</div>';
  };

  RestorationQuiz.prototype._renderResult = function () {
    var s = this.state;
    var result = s.result || { verdict: 'potential', flags: {} };
    var verdict = result.verdict === 'probably_not' ? 'probably_not' : (result.verdict === 'strong' ? 'strong' : 'potential');
    var meta = VERDICT_META[verdict];
    var reasons = buildReasons(s.answers, result);

    var reasonsHtml = reasons.map(function (r) {
      return '<div class="frcq-reason"><span class="frcq-r-dot"></span><span>' + r + '</span></div>';
    }).join('');

    var frontFile = s.photos.front;
    var backFile = s.photos.back;

    return '<div class="frcq-screen frcq-active frcq-result ' + meta.cls + '">' +
      '<div class="frcq-topbar" style="margin-bottom:6px;"><span style="width:32px"></span><span style="flex:1"></span>' +
      '<button class="frcq-iconbtn" title="Close" data-action="close">' + ICONS.close + '</button></div>' +

      '<div class="frcq-verdict-icon">' + meta.icon + '</div>' +
      '<div class="frcq-verdict-eyebrow">' + meta.eyebrow + '</div>' +
      '<h2 class="frcq-verdict-title">' + VERDICT_TITLE[verdict] + '</h2>' +
      '<p class="frcq-verdict-explain">' + VERDICT_EXPLAIN[verdict] + '</p>' +

      '<div class="frcq-factors-label">What we’re seeing</div>' +
      '<div class="frcq-reasons">' + reasonsHtml + '</div>' +

      '<div class="frcq-next-step"><div class="frcq-label">Our recommendation</div><p>' + VERDICT_NEXT[verdict] + '</p></div>' +

      (s.submitOpen ? '' : '<button class="frcq-btn-primary" data-action="open-submit">' + meta.ctaLabel + '</button>') +

      '<div class="frcq-submit-wrap' + (s.submitOpen && s.submitAnimated ? ' frcq-open' : '') + '">' +
      '<hr class="frcq-divider">' +
      '<div class="frcq-section-title">' + meta.sectionTitle + '</div>' +
      '<div class="frcq-section-sub">A visual check always beats a questionnaire. Photos are optional but make our answer far more accurate.</div>' +
      '<div class="frcq-upload-row">' +
      this._uploadTile('front', 'Front photo', frontFile) +
      this._uploadTile('back', 'Back photo', backFile) +
      '</div>' +
      '<div class="frcq-field"><label>Name</label><input type="text" placeholder="Your name" data-field="name" value="' + (s.contactName || '') + '"></div>' +
      '<div class="frcq-field"><label>Email</label><input type="email" placeholder="you@email.com" data-field="email" value="' + (s.contactEmail || '') + '"></div>' +
      '<div class="frcq-cta-final"><button class="frcq-btn-primary" data-action="submit">Send My Card for a Visual Assessment</button></div>' +
      '<p class="frcq-submit-note">We’ll only use these details to follow up about your card.</p>' +
      '</div>' +
      '</div>';
  };

  RestorationQuiz.prototype._uploadTile = function (key, label, file) {
    var preview = file ? '<img src="' + file.previewUrl + '">' : '';
    var labelHtml = file ? '<span class="frcq-file-label">Change</span>' : ('<span>' + ICONS.upload + '</span><span>' + label + '</span>');
    return '<label class="frcq-upload-tile' + (file ? ' frcq-has-file' : '') + '">' + preview + labelHtml +
      '<input type="file" accept="image/*" data-photo="' + key + '"></label>';
  };

  // ---- event binding --------------------------------------------------
  RestorationQuiz.prototype._bind = function () {
    var self = this;
    var s = this.state;

    this._root.querySelectorAll('[data-action]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        var action = el.getAttribute('data-action');
        var value = el.getAttribute('data-value');

        if (action === 'start') self.start();
        else if (action === 'back') self.back();
        else if (action === 'close') { /* host page decides what "close" does */ if (typeof self.onClose === 'function') self.onClose(); }
        else if (action === 'answer') self._setAnswer(s.qIndex, value);
        else if (action === 'answer-conditional') self._setConditionalAnswer(s.qIndex, value);
        else if (action === 'answer-damage-queue') self._setDamageQueueAnswer(value);
        else if (action === 'toggle-damage') self._toggleDamage(value);
        else if (action === 'continue-multi') self._advanceFromQuestion(s.qIndex);
        else if (action === 'open-submit') {
          s.submitOpen = true;
          s.submitAnimated = false;
          self._render();
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              s.submitAnimated = true;
              self._render();
              self._scrollToSubmit();
            });
          });
        }
        else if (action === 'submit') self._handleSubmit();
      });
    });

    this._root.querySelectorAll('[data-field]').forEach(function (el) {
      el.addEventListener('input', function () {
        if (el.getAttribute('data-field') === 'name') s.contactName = el.value;
        if (el.getAttribute('data-field') === 'email') s.contactEmail = el.value;
      });
    });

    this._root.querySelectorAll('[data-photo]').forEach(function (el) {
      el.addEventListener('change', function () {
        var key = el.getAttribute('data-photo');
        var file = el.files && el.files[0];
        if (!file) return;
        var reader = new FileReader();
        reader.onload = function () {
          s.photos[key] = { file: file, previewUrl: reader.result };
          self._render();
          self._scrollToSubmit();
        };
        reader.readAsDataURL(file);
      });
    });
  };

  RestorationQuiz.prototype._scrollToSubmit = function () {
    var wrap = this._root.querySelector('.frcq-submit-wrap');
    if (wrap && wrap.scrollIntoView) wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  RestorationQuiz.prototype._handleSubmit = function () {
    var s = this.state;
    this.onSubmit({
      answers: s.answers,
      result: s.result,
      contact: { name: s.contactName || '', email: s.contactEmail || '' },
      photos: {
        front: s.photos.front ? s.photos.front.file : null,
        back: s.photos.back ? s.photos.back.file : null,
      },
    });
  };

  window.RestorationQuiz = RestorationQuiz;
})();
